/**
 * Vue Router 路由守卫 — 动态路由 & 权限控制核心
 */
import router from './index'
import { useUserStore } from '@/Store/user'
import { usePermissionStore } from '@/Store/permission'
import { getToken } from '@/utils/auth'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'

NProgress.configure({ showSpinner: false })

/** 白名单路由 */
const whiteList = ['/login']

// ✅ 使用标志控制路由添加状态
let isDynamicRoutesAdded = false
let isInitializing = false
let isReAddingRoutes = false // 🔥 新增：防止重新添加时循环

router.beforeEach(async (to, from, next) => {
  NProgress.start()

  // 设置页面标题
  const title = to.meta?.title
  if (title) {
    document.title = typeof title === 'string' ? title : 'KP-RAG'
  }

  const hasToken = getToken()

  if (hasToken) {
    // ========== 已登录 ==========
    if (to.path === '/login') {
      next({ path: '/' })
      NProgress.done()
      return
    }

    const userStore = useUserStore()
    const permissionStore = usePermissionStore()

    // ✅ 如果正在初始化，等待完成
    if (isInitializing) {
      await new Promise(resolve => {
        const checkReady = () => {
          if (!isInitializing) {
            resolve()
          } else {
            setTimeout(checkReady, 100)
          }
        }
        checkReady()
      })
      next({ path: to.path, replace: true })
      return
    }

    // ✅ 如果正在重新添加路由，直接放行避免循环
    if (isReAddingRoutes) {
      isReAddingRoutes = false
      next()
      return
    }

    // ✅ 检查用户信息是否已加载
    if (!userStore.hasUser) {
      // 首次进入 → 初始化
      isInitializing = true
      try {
        // 并行获取用户信息和系统配置
        await Promise.all([
          userStore.getUserInfo(),
          userStore.getSystem_conf(),
        ])

        // 生成并添加动态路由
        const accessRoutes = await permissionStore.generateRoutes()
        accessRoutes.forEach(route => {
          router.addRoute('Layout', route)
        })

        // 添加 404 兜底
        if (!isDynamicRoutesAdded) {
          router.addRoute('Layout', {
            path: ':pathMatch(.*)*',
            component: () => import('@/views/error-page/404.vue'),
            meta: { title: '404', noCache: true },
            hidden: true,
          })
          isDynamicRoutesAdded = true
        }

        isInitializing = false

        // 重放导航
        next({ path: to.path, replace: true })
        return
      } catch (error) {
        console.error('[Router Guard] 路由初始化失败:', error)
        isInitializing = false
        userStore.resetToken()
        next(`/login?redirect=${to.path}`)
        NProgress.done()
        return
      }
    }

    // ✅ 用户信息已加载，检查动态路由是否有效
    if (isDynamicRoutesAdded) {
      // 🔥 关键修复：检查当前路由是否匹配到具体页面（不是404）
      const resolved = router.resolve(to)
      const routeExists = resolved.matched.length > 0 &&
        !resolved.matched.some(route => route.path === '/:pathMatch(.*)*')

      // 🔥 如果路由不存在且不是白名单路径，尝试重新添加
      if (!routeExists && to.path !== '/' && !whiteList.includes(to.path)) {
        console.warn('[Router Guard] 动态路由未注册，重新添加')

        // 🔥 设置标志，防止循环
        isReAddingRoutes = true

        const savedRoutes = permissionStore.routes
        if (savedRoutes && savedRoutes.length) {
          // 先清空旧的动态路由（可选）
          // 注意：Vue Router 4 不支持直接移除路由，只能重新添加
          savedRoutes.forEach(route => {
            // 检查是否已存在，避免重复添加
            const existingRoute = router.hasRoute(route.name)
            if (!existingRoute) {
              router.addRoute('Layout', route)
            }
          })

          // 确保404路由存在
          if (!router.hasRoute('404')) {
            router.addRoute('Layout', {
              path: ':pathMatch(.*)*',
              name: '404',
              component: () => import('@/views/error-page/404.vue'),
              meta: { title: '404', noCache: true },
              hidden: true,
            })
          }

          // ✅ 使用 replace 并标记已处理
          next({ path: to.path, replace: true })
          return
        }
      }
    }

    // 正常放行
    next()
  } else {
    // ========== 未登录 ==========
    if (whiteList.includes(to.path)) {
      next()
      return
    }

    const permissionStore = usePermissionStore()

    // 检查公开路由是否已加载
    if (permissionStore.routes.length > 0 && isDynamicRoutesAdded) {
      next('/login')
      return
    }

    // 加载公开路由
    try {
      const userStore = useUserStore()
      await userStore.getSystem_conf()

      const accessRoutes = await permissionStore.generateNopermission()
      accessRoutes.forEach(route => {
        router.addRoute('Layout', route)
      })

      if (!isDynamicRoutesAdded) {
        router.addRoute('Layout', {
          path: ':pathMatch(.*)*',
          name: '404',
          component: () => import('@/views/error-page/404.vue'),
          meta: { title: '404', noCache: true },
          hidden: true,
        })
        isDynamicRoutesAdded = true
      }

      next({ path: to.path, replace: true })
    } catch (error) {
      console.error('[Router Guard] 获取公开路由失败:', error)
      next(`/login?redirect=${to.path}`)
      NProgress.done()
    }
  }
})

router.afterEach(() => {
  NProgress.done()
})